import { Box, Button, Divider, Stack } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  useAddUserZoneAssociationUsersUserIdZonesZoneIdPostMutation,
  useCreateUserUsersPostMutation,
} from "api";
import { User } from "api/generatedAuth";
import { RoleInput, TextInput, ZoneInput } from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";
import { useNavigate } from "hooks/useNavigate";
import { IZone } from "interfaces/zone.interface";

import { UserFormSchema, userFormSchema } from "./userFormSchema";

import { FieldColumns } from "../../components/FieldColumns";
import { PageHeader } from "../../components/PageHeader";

type CreateUserProps = {
  zones: IZone[];
  authUser?: Pick<User, "zones" | "preferences">;
};

export function CreateUserForm({ zones, authUser }: CreateUserProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [createUser, { isLoading: isCreatingUser }] =
    useCreateUserUsersPostMutation();
  const [addZones, { isLoading: isAddingZone }] =
    useAddUserZoneAssociationUsersUserIdZonesZoneIdPostMutation();

  const { control, handleSubmit, setError } = useForm({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      zones: authUser?.zones?.data?.map((zone) => zone.id) ?? [],
      roles: [],
      preferences: {
        language: authUser?.preferences?.language ?? "en",
      },
      metadata: {},
    },
  });

  const onSubmit = async (data: UserFormSchema) => {
    const { data: userData, error } = await createUser({
      userCreate: {
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        profession: data.profession ?? "",
        roles: data.roles,
        preferences: data.preferences,
        metadata: data.metadata,
      },
    });

    if (
      error &&
      "data" in error &&
      error.data &&
      typeof error.data === "object" &&
      "detail" in error.data &&
      error.data.detail === "The user already exists"
    ) {
      setError("email", {
        message: t("error_message_employee_email_already_exists"),
      });
      return;
    }

    const userId = userData?.data?.id;

    if (userId) {
      await Promise.all(
        data.zones.map((zoneId) => addZones({ userId: userId, zoneId })),
      );

      navigate(`../users/${userId}`);
    }
  };

  return (
    <Stack
      component={"form"}
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        gap: 2,
      }}
    >
      <PageHeader title={t("create_employee")} />
      <SectionCard title={t("employee_information")}>
        <FieldColumns>
          <TextInput
            label={t("First Name")}
            control={control}
            fieldName={"first_name"}
            placeholder={t("user_first_name_placeholder")}
            required
          />
          <TextInput
            label={t("Last Name")}
            control={control}
            fieldName={"last_name"}
            placeholder={t("user_last_name_placeholder")}
            required
          />
          <TextInput
            label={t("Email")}
            type={"email"}
            control={control}
            fieldName={"email"}
            placeholder={t("user_email_placeholder")}
            required
          />
          <TextInput
            label={t("Job Title")}
            control={control}
            fieldName={"profession"}
            placeholder={t("user_title_placeholder")}
          />
        </FieldColumns>
      </SectionCard>
      <SectionCard title={t("Zones and Roles")}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            width: "100%",
            flexDirection: { xs: "column", lg: "row" },
          }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 2 }}
          >
            <ZoneInput
              label={t("Zones")}
              control={control}
              fieldName={"zones"}
              zones={zones}
              errorMap={{
                too_small: t("error_message_user_zones"),
              }}
            />
          </Box>
          <Divider sx={{ display: { xs: "initial", lg: "none" } }} />
          <Box
            sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 2 }}
          >
            <RoleInput
              label={t("Role(s)")}
              control={control}
              fieldName={"roles"}
              errorMap={{
                too_small: t("error_message_user_roles"),
              }}
            />
          </Box>
        </Box>
      </SectionCard>
      <Stack
        direction={"row"}
        sx={{ gap: 2, justifyContent: "flex-end", paddingInline: 2 }}
      >
        <Button variant={"outlined"} component={Link} to={"../users"}>
          {t("Cancel")}
        </Button>
        <Button type={"submit"} loading={isCreatingUser || isAddingZone}>
          {t("Create")}
        </Button>
      </Stack>
    </Stack>
  );
}
